/**
 * Notifications Component (UI)
 * 
 * Layer: Component (UI) – chỉ render JSX, gọi hook domain từ features.
 * Chức năng: Hiển thị danh sách thông báo với các tab (tất cả, chưa đọc, hôm nay)
 * và xử lý UI interactions (click, mark as read, etc.)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { List, Button, Empty, Tabs, Spin, Typography, Avatar, Space, Checkbox, Popconfirm } from 'antd';
import {
  ClockCircleOutlined,
  UserOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { Notification } from '../../services/notification.service';
import { useNotifications } from '../../features/notifications/useNotifications';
import { buildFullUrl } from '../../contexts/AvatarContext';
import sdtcIcon from '../../assets/sdtc-icon.svg';
import burgerIcon from '../../assets/icon-notification/burger.svg';
import likeIcon from '../../assets/icon-notification/like.svg';
import unlikeIcon from '../../assets/icon-notification/unlike.svg';
import penIcon from '../../assets/icon-notification/pen.svg';
import phoneIcon from '../../assets/icon-notification/phone.svg';
import tickIcon from '../../assets/icon-notification/tick.svg';
import styles from './Notifications.module.css';

const { Text } = Typography;

const SeenTickIcon: React.FC = () => (
  <img src={tickIcon} alt="" aria-hidden="true" className={styles.tickIcon} />
);

interface NotificationsProps {
  onClose?: () => void;
  onMarkAsRead?: () => void;
  refreshTrigger?: number; // Trigger refresh khi giá trị này thay đổi
}

// Component để render avatar với auth (giống PersonnelAvatar)
interface NotificationAvatarProps {
  avatarUrl: string | null | undefined;
  fetchAvatarAsDataUri: (url: string) => Promise<string | null>;
  cacheRef: React.MutableRefObject<Map<string, string>>;
}

const NotificationAvatar: React.FC<NotificationAvatarProps> = ({ avatarUrl, fetchAvatarAsDataUri, cacheRef }) => {
  const [avatarDataUri, setAvatarDataUri] = useState<string | null>(null);
  const [avatarError, setAvatarError] = useState(false);
  const [useDirectUrl, setUseDirectUrl] = useState(false);

  useEffect(() => {
    // Reset state khi avatarUrl thay đổi
    setAvatarDataUri(null);
    setAvatarError(false);
    setUseDirectUrl(false);

    const isValidUrl = avatarUrl?.startsWith('http://') || avatarUrl?.startsWith('https://');
    
    if (!isValidUrl) {
      return;
    }

    // Check cache first
    const cached = cacheRef.current.get(avatarUrl!);
    if (cached) {
      setAvatarDataUri(cached);
      setAvatarError(false);
      return;
    }

    fetchAvatarAsDataUri(avatarUrl!)
      .then((dataUri) => {
        if (dataUri) {
          // Cache the result
          cacheRef.current.set(avatarUrl!, dataUri);
          setAvatarDataUri(dataUri);
          setAvatarError(false);
          setUseDirectUrl(false);
        } else {
          // Fallback: dùng URL trực tiếp nếu fetch data URI thất bại
          setUseDirectUrl(true);
          setAvatarError(false);
        }
      })
      .catch((error) => {
        console.error('Error fetching avatar:', error);
        // Fallback: dùng URL trực tiếp
        setUseDirectUrl(true);
        setAvatarError(false);
      });
  }, [avatarUrl, fetchAvatarAsDataUri, cacheRef]);

  // Sử dụng data URI nếu có, nếu không thì dùng URL trực tiếp (fallback)
  const avatarSrc = avatarDataUri && !avatarError ? avatarDataUri : (useDirectUrl && avatarUrl ? avatarUrl : undefined);
  const showAvatar = !!avatarSrc;

  return (
    <Avatar
      src={showAvatar ? avatarSrc : undefined}
      icon={!showAvatar ? <UserOutlined /> : undefined}
      size={32}
      shape="circle"
      className={styles.avatarFlexShrink}
      onError={() => {
        // Nếu đang dùng data URI mà lỗi, thử fallback về URL trực tiếp
        if (avatarDataUri && !useDirectUrl) {
          setUseDirectUrl(true);
          setAvatarDataUri(null);
          setAvatarError(false);
        } else {
          setAvatarError(true);
        }
        return false;
      }}
    />
  );
};

export const Notifications: React.FC<NotificationsProps> = ({ onClose, onMarkAsRead, refreshTrigger }) => {
  // Sử dụng hook domain để lấy toàn bộ logic nghiệp vụ
  const {
    loading,
    error,
    activeTab,
    setActiveTab,
    filteredNotifications,
    unreadCount,
    handleNotificationClick,
    markAllAsRead,
    deleteNotifications,
    fetchAvatarAsDataUri,
    avatarCacheRef,
    isBirthdayNotification,
    isUpdateRequestNotification,
    isHRUpdateNotification,
    isNewUpdateRequestNotification,
    getBirthdayPersonName,
    getBirthdayPersonAvatar,
    getApproverInfo,
    getRejectorInfo,
    getHRUpdaterInfo,
    getRequesterInfo,
  } = useNotifications(onMarkAsRead, refreshTrigger);

  const [isSelecting, setIsSelecting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setIsSelecting(false);
  }, []);

  useEffect(() => {
    // Khi đổi tab / refresh trigger, reset selection để tránh nhầm lẫn
    setSelectedIds(new Set());
  }, [activeTab, refreshTrigger]);

  const toggleSelected = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleDeleteSelected = useCallback(async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    await deleteNotifications(ids);
    clearSelection();
  }, [selectedIds, deleteNotifications, clearSelection]);

  // Wrapper để đóng dropdown khi click notification
  const handleClick = useCallback(async (item: Notification) => {
    await handleNotificationClick(item);
    onClose?.();
  }, [handleNotificationClick, onClose]);

  // Helper để lấy icon cho notification (logic UI, không phải domain)
  const getNotificationIcon = useCallback((item: Notification): string | null => {
    // Birthday notification → burger icon
    if (isBirthdayNotification(item)) {
      return burgerIcon;
    }
    
    // Approved update request → like icon
    if (isUpdateRequestNotification(item) && getApproverInfo(item)) {
      return likeIcon;
    }
    
    // Rejected update request → unlike icon
    if (isUpdateRequestNotification(item) && getRejectorInfo(item)) {
      return unlikeIcon;
    }
    
    // HR update notification → pen icon
    if (isHRUpdateNotification(item)) {
      return penIcon;
    }
    
    // New update request → phone icon
    if (isNewUpdateRequestNotification(item)) {
      return phoneIcon;
    }
    
    return null;
  }, [isBirthdayNotification, isUpdateRequestNotification, isHRUpdateNotification, isNewUpdateRequestNotification, getApproverInfo, getRejectorInfo]);

  // Render title với highlight (logic UI)
  const renderHighlightedTitle = useCallback((title: string, highlights?: string[]) => {
    if (!highlights || highlights.length === 0) {
      return <Text strong>{title}</Text>;
    }

    const parts: (string | React.ReactElement)[] = [];
    let lastIndex = 0;
    let key = 0;

    // Tìm tất cả các vị trí cần highlight
    const matches: Array<{ start: number; end: number; text: string }> = [];
    highlights.forEach((highlight) => {
      const regex = new RegExp(highlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
      let match;
      while ((match = regex.exec(title)) !== null) {
        matches.push({
          start: match.index,
          end: match.index + match[0].length,
          text: match[0],
        });
      }
    });

    // Sắp xếp matches theo vị trí
    matches.sort((a, b) => a.start - b.start);

    // Merge các matches trùng lặp hoặc gần nhau
    const mergedMatches: Array<{ start: number; end: number; text: string }> = [];
    matches.forEach((match) => {
      const last = mergedMatches[mergedMatches.length - 1];
      if (last && match.start <= last.end) {
        last.end = Math.max(last.end, match.end);
        last.text = title.substring(last.start, last.end);
      } else {
        mergedMatches.push({ ...match });
      }
    });

    // Tạo các phần text
    mergedMatches.forEach((match) => {
      if (match.start > lastIndex) {
        parts.push(title.substring(lastIndex, match.start));
      }
      parts.push(
        <Text key={key++} strong className={styles.highlightText}>
          {match.text}
        </Text>
      );
      lastIndex = match.end;
    });

    if (lastIndex < title.length) {
      parts.push(title.substring(lastIndex));
    }

    return <Text strong>{parts}</Text>;
  }, []);

  const renderPrimaryAction = useCallback((item: Notification) => {
    if (item.id === '1' && item.title === 'Nhắc nhở chấm công') {
      return (
        <Button
          type="primary"
          size="small"
          className={styles.buttonMarginTop}
          onClick={() => {
            // Navigate đến trang chấm công
            window.location.href = '/';
            onClose?.();
          }}
        >
          Chấm công ngay
        </Button>
      );
    }

    if (item.id === '2') {
      return (
        <Space className={styles.spaceMarginTop}>
          <Button type="primary" size="small">
            Duyệt
          </Button>
          <Button size="small">
            Từ chối
          </Button>
        </Space>
      );
    }

    return null;
  }, [onClose]);

  const tabItems = [
    {
      key: 'all',
      label: (
        <span>
          Tất cả
          {/* {unreadCount > 0 && (
            <Badge count={unreadCount} size="small" className={styles.badgeMarginLeft} />
          )} */}
        </span>
      ),
    },
    {
      key: 'unread',
      label: (
        <span>
          Chưa đọc
          {/* {unreadCount > 0 && (
            <Badge count={unreadCount} size="small" className={styles.badgeMarginLeft} />
          )} */}
        </span>
      ),
    },
    {
      key: 'today',
      label: (
        <span>
          Hôm nay
          {/* {todayUnreadCount > 0 && (
            <Badge count={todayUnreadCount} size="small" className={styles.badgeMarginLeft} />
          )} */}
        </span>
      ),
    },
  ];

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Text strong className={styles.headerTitle}>Thông báo</Text>
        <Space size={8}>
          {isSelecting ? (
            <>
              <Button type="link" size="small" onClick={clearSelection} disabled={loading}>
                Hủy
              </Button>
              <Popconfirm
                title={`Xóa ${selectedIds.size} thông báo?`}
                okText="Xóa"
                cancelText="Hủy"
                onConfirm={handleDeleteSelected}
                disabled={selectedIds.size === 0 || loading}
              >
                <Button
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  disabled={selectedIds.size === 0 || loading}
                >
                  Xóa ({selectedIds.size})
                </Button>
              </Popconfirm>
            </>
          ) : (
            <>
              <Button
                type="link"
                size="small"
                onClick={() => setIsSelecting(true)}
                disabled={filteredNotifications.length === 0 || loading}
              >
                Chọn
              </Button>
              {unreadCount > 0 && (
                <Button
                  type="link"
                  size="small"
                  icon={<SeenTickIcon />}
                  onClick={markAllAsRead}
                  disabled={loading}
                  className={styles.iconOnlyButton}
                  aria-label="Đánh dấu tất cả đã đọc"
                  title="Đánh dấu tất cả đã đọc"
                />
              )}
            </>
          )}
        </Space>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key as 'all' | 'unread' | 'today')}
        items={tabItems}
        size="small"
        className={styles.tabs}
      />

      <div className={styles.listContainer}>
        {error && (
          <div className={styles.errorMessage}>
            {error}
          </div>
        )}
        {loading ? (
          <div className={styles.loadingContainer}>
            <Spin />
          </div>
        ) : filteredNotifications.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Không có thông báo"
            className={styles.emptyPadding}
          />
        ) : (
            <List
            dataSource={filteredNotifications}
            renderItem={(item) => (
              <List.Item
                className={`${styles.notificationItem} ${!item.read ? styles.unreadItem : ''} ${
                  isBirthdayNotification(item) ? styles.birthdayItem
                    : ''
                } ${styles.clickableItem}`}
                onClick={() => {
                  if (isSelecting) {
                    toggleSelected(String(item.id));
                    return;
                  }
                  handleClick(item);
                }}
              >
                <List.Item.Meta
                  avatar={
                    (() => {
                      const idStr = String(item.id);
                      const checkbox = isSelecting ? (
                        <Checkbox
                          checked={selectedIds.has(idStr)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggleSelected(idStr)}
                        />
                      ) : null;

                      const notificationIcon = getNotificationIcon(item);
                      if (notificationIcon) {
                        return (
                          <div className={styles.avatarRow}>
                            {checkbox}
                            <Avatar
                              src={notificationIcon}
                              alt="Notification icon"
                              size={24}
                              className={styles.iconAvatar}
                            />
                          </div>
                        );
                      }
                      // Fallback to existing avatar logic
                      if (item.avatar?.trim()) {
                        return (
                          <div className={styles.avatarRow}>
                            {checkbox}
                            <NotificationAvatar
                              avatarUrl={buildFullUrl(item.avatar)}
                              fetchAvatarAsDataUri={fetchAvatarAsDataUri}
                              cacheRef={avatarCacheRef}
                            />
                          </div>
                        );
                      }
                      if (item.title === 'Nhắc nhở chấm công' || item.relatedResourceType === 'attendance') {
                        return (
                          <div className={styles.avatarRow}>
                            {checkbox}
                            <Avatar src={sdtcIcon} />
                          </div>
                        );
                      }
                      return (
                        <div className={styles.avatarRow}>
                          {checkbox}
                          <Avatar icon={<UserOutlined />} />
                        </div>
                      );
                    })()
                  }
                  title={
                    <div className={styles.titleRow}>
                      <div className={styles.titleContainer}>
                        {renderHighlightedTitle(item.title, item.titleHighlights)}
                        {!item.read && <div className={styles.unreadDot} />}
                      </div>
                      <Text type="secondary" className={styles.time}>
                        {item.time}
                      </Text>
                    </div>
                  }
                  description={
                    <div>
                      {isBirthdayNotification(item) && (
                        <div className={styles.birthdayMeta}>
                          <Avatar
                            className={styles.birthdayMetaAvatar}
                            src={getBirthdayPersonAvatar(item) || undefined}
                            icon={!getBirthdayPersonAvatar(item) ? <UserOutlined /> : undefined}
                          />
                          <span className={styles.birthdayMetaText}>
                            <Text strong className={styles.birthdayName}>
                              {getBirthdayPersonName(item) || 'Nhân sự'}
                            </Text>
                            <Text type="secondary" className={styles.birthdayHint}>
                              Hôm nay sinh nhật
                            </Text>
                          </span>
                        </div>
                      )}
                      {(() => {
                        // Hiển thị thông tin người gửi yêu cầu cho "Yêu cầu cập nhật thông tin mới"
                        // Bên trên: icon + "Yêu cầu cập nhật thông tin mới" (title) - tự động hiển thị
                        // Bên dưới: avatar + "[tên] đã gửi yêu cầu cập nhật thông tin" (message với tên in đậm)
                        if (isNewUpdateRequestNotification(item)) {
                          const requester = getRequesterInfo(item);
                          
                          // Lấy message chính
                          let mainMessage = item.message || '';
                          
                          // LUÔN hiển thị tên người gửi yêu cầu (in đậm)
                          let messageParts: (string | React.ReactElement)[] = [];
                          let displayName: string | null = null;
                          
                          // Ưu tiên 1: Lấy tên từ requester.fullName (backend gửi)
                          if (requester?.fullName) {
                            displayName = requester.fullName;
                          }
                          // Ưu tiên 2: Parse tên từ message
                          else {
                            const nameMatch = mainMessage.match(/^(.+?)\s+đã\s+gửi/i);
                            if (nameMatch && nameMatch[1]) {
                              displayName = nameMatch[1].trim();
                            }
                          }
                          
                          // Tạo message với tên in đậm
                          if (displayName) {
                            // Nếu message đã có tên này, tách và in đậm
                            if (mainMessage.includes(displayName)) {
                              const nameIndex = mainMessage.indexOf(displayName);
                              const beforeName = mainMessage.substring(0, nameIndex);
                              const afterName = mainMessage.substring(nameIndex + displayName.length);
                              messageParts = [
                                beforeName,
                                <Text key="name" strong className={styles.boldName}>{displayName}</Text>,
                                afterName
                              ];
                            } 
                            // Fallback: thêm tên vào đầu message
                            else {
                              messageParts = [
                                <Text key="name" strong className={styles.boldName}>{displayName}</Text>,
                                ' ',
                                mainMessage
                              ];
                            }
                          } else {
                            // Không có tên, giữ nguyên message nhưng vẫn hiển thị format mới
                            messageParts = [mainMessage];
                          }
                          
                          // Xử lý avatar URL - ưu tiên requester.avatar, sau đó item.avatar
                          let avatarUrl: string | undefined = undefined;
                          if (requester?.avatar) {
                            const avatar = requester.avatar.trim();
                            if (avatar) {
                              avatarUrl = avatar.startsWith('http://') || avatar.startsWith('https://') 
                                ? avatar 
                                : buildFullUrl(avatar) || undefined;
                            }
                          } else if (item.avatar?.trim()) {
                            const fullUrl = buildFullUrl(item.avatar);
                            avatarUrl = fullUrl || undefined;
                          }
                          
                          return (
                            <>
                              <div className={`${styles.birthdayMeta} ${styles.metaMarginBottom}`}>
                                <NotificationAvatar
                                  avatarUrl={avatarUrl}
                                  fetchAvatarAsDataUri={fetchAvatarAsDataUri}
                                  cacheRef={avatarCacheRef}
                                />
                                <span className={styles.birthdayMetaText}>
                                  <Text type="secondary" className={styles.message}>
                                    {messageParts}
                                  </Text>
                                </span>
                              </div>
                            </>
                          );
                        }
                        
                        // Hiển thị thông tin người duyệt/từ chối cho user-update-request (đã được phê duyệt/từ chối)
                        if (isUpdateRequestNotification(item)) {
                          const approver = getApproverInfo(item);
                          const rejector = getRejectorInfo(item);
                          
                          if (approver || rejector) {
                            const person = approver || rejector;
                            
                            // Lấy message chính (bỏ phần "Lý do:" nếu có trong message)
                            let mainMessage = item.message || '';
                            if (mainMessage.includes('Lý do:')) {
                              mainMessage = mainMessage.split('Lý do:')[0].trim();
                            }
                            
                            // Parse message để tách tên và làm tên in đậm
                            // Format: "[Tên] đã duyệt/từ chối..."
                            const nameMatch = mainMessage.match(/^(.+?)\s+đã\s+(duyệt|từ chối)/);
                            let messageParts: (string | React.ReactElement)[] = [];
                            
                            if (nameMatch && person?.fullName) {
                              const name = person.fullName;
                              const beforeName = mainMessage.substring(0, mainMessage.indexOf(name));
                              const afterName = mainMessage.substring(mainMessage.indexOf(name) + name.length);
                              
                              messageParts = [
                                beforeName,
                                <Text key="name" strong className={styles.boldName}>{name}</Text>,
                                afterName
                              ];
                            } else {
                              messageParts = [mainMessage];
                            }
                            
                            // Lấy lý do từ note hoặc data.note (chỉ hiển thị cho từ chối)
                            const reason = rejector ? (item.note || item.data?.note) : null;
                            
                            // Xử lý avatar URL
                            let avatarUrl: string | undefined = undefined;
                            if (person?.avatar) {
                              const avatar = person.avatar.trim();
                              if (avatar) {
                                avatarUrl = avatar.startsWith('http://') || avatar.startsWith('https://') 
                                  ? avatar 
                                  : buildFullUrl(avatar) || undefined;
                              }
                            }
                            
                            return (
                              <>
                                <div className={`${styles.birthdayMeta} ${styles.metaMarginBottom}`}>
                                  <NotificationAvatar
                                    avatarUrl={avatarUrl}
                                    fetchAvatarAsDataUri={fetchAvatarAsDataUri}
                                    cacheRef={avatarCacheRef}
                                  />
                                  <span className={styles.birthdayMetaText}>
                                    <Text type="secondary" className={styles.message}>
                                      {messageParts}
                                    </Text>
                                  </span>
                                </div>
                                {reason && (
                                  <div className={styles.noteContainer}>
                                    <Text type="secondary" className={styles.noteLabel}>Lý do:</Text>
                                    <div className={styles.noteBlock}>
                                      <Text type="secondary">"{reason}"</Text>
                                    </div>
                                  </div>
                                )}
                              </>
                            );
                          }
                        }
                        
                        // Hiển thị thông tin người HR cập nhật cho "Thông tin đã được cập nhật"
                        // Bên trên: icon + "Thông tin đã được cập nhật" (title) - tự động hiển thị
                        // Bên dưới: avatar + "[tên] đã cập nhật thông tin của bạn" (message với tên in đậm)
                        if (isHRUpdateNotification(item)) {
                          const updater = getHRUpdaterInfo(item);
                          
                          // Lấy message chính (bỏ phần "Lý do:" nếu có trong message)
                          let mainMessage = item.message || '';
                          if (mainMessage.includes('Lý do:')) {
                            mainMessage = mainMessage.split('Lý do:')[0].trim();
                          }
                          
                          // LUÔN thay "HR" bằng tên người cập nhật (in đậm)
                          let messageParts: (string | React.ReactElement)[] = [];
                          let displayName: string | null = null;
                          
                          // Ưu tiên 1: Lấy tên từ updater.fullName (backend gửi)
                          if (updater?.fullName) {
                            displayName = updater.fullName;
                          }
                          // Ưu tiên 2: Parse tên từ message nếu message đã có tên (không phải "HR")
                          else {
                            const nameMatch = mainMessage.match(/^(.+?)\s+đã\s+cập nhật/i);
                            if (nameMatch && nameMatch[1]) {
                              const parsedName = nameMatch[1].trim();
                              if (parsedName.toLowerCase() !== 'hr') {
                                displayName = parsedName;
                              }
                            }
                          }
                          
                          // Tạo message với tên in đậm
                          if (displayName) {
                            // Nếu message đã có tên này, tách và in đậm
                            if (mainMessage.includes(displayName)) {
                              const nameIndex = mainMessage.indexOf(displayName);
                              const beforeName = mainMessage.substring(0, nameIndex);
                              const afterName = mainMessage.substring(nameIndex + displayName.length);
                              messageParts = [
                                beforeName,
                                <Text key="name" strong className={styles.boldName}>{displayName}</Text>,
                                afterName
                              ];
                            } 
                            // Nếu message có "HR", thay bằng tên
                            else if (mainMessage.match(/HR\s+đã\s+cập nhật/i)) {
                              const replacedMessage = mainMessage.replace(/HR\s+/i, '');
                              messageParts = [
                                <Text key="name" strong className={styles.boldName}>{displayName}</Text>,
                                ' ',
                                replacedMessage
                              ];
                            }
                            // Fallback: thêm tên vào đầu message
                            else {
                              messageParts = [
                                <Text key="name" strong className={styles.boldName}>{displayName}</Text>,
                                ' ',
                                mainMessage
                              ];
                            }
                          } else {
                            // Không có tên, giữ nguyên message nhưng vẫn hiển thị format mới
                            messageParts = [mainMessage];
                          }
                          
                          // Xử lý avatar URL - ưu tiên updater.avatar, sau đó item.avatar
                          let avatarUrl: string | undefined = undefined;
                          if (updater?.avatar) {
                            const avatar = updater.avatar.trim();
                            if (avatar) {
                              avatarUrl = avatar.startsWith('http://') || avatar.startsWith('https://') 
                                ? avatar 
                                : buildFullUrl(avatar) || undefined;
                            }
                          } else if (item.avatar?.trim()) {
                            const fullUrl = buildFullUrl(item.avatar);
                            avatarUrl = fullUrl || undefined;
                          }
                          
                          return (
                            <>
                              <div className={`${styles.birthdayMeta} ${styles.metaMarginBottom}`}>
                                <NotificationAvatar
                                  avatarUrl={avatarUrl}
                                  fetchAvatarAsDataUri={fetchAvatarAsDataUri}
                                  cacheRef={avatarCacheRef}
                                />
                                <span className={styles.birthdayMetaText}>
                                  <Text type="secondary" className={styles.message}>
                                    {messageParts}
                                  </Text>
                                </span>
                              </div>
                            </>
                          );
                        }
                        
                        // Tách message nếu có chứa "Lý do:" (cho các notification khác không phải user-update-request, HR update và new update request)
                        if (item.message && item.message.includes('Lý do:')) {
                          const parts = item.message.split('Lý do:');
                          const messagePart = parts[0].trim();
                          const reasonPart = parts[1]?.trim();
                          
                          return (
                            <>
                              {messagePart && (
                                <Text type="secondary" className={styles.message}>
                                  {messagePart}
                                </Text>
                              )}
                              {reasonPart && (
                                <div className={styles.noteContainer}>
                                  <Text type="secondary" className={styles.noteLabel}>Lý do:</Text>
                                  <div className={styles.noteBlock}>
                                    <Text type="secondary">"{reasonPart}"</Text>
                                  </div>
                                </div>
                              )}
                            </>
                          );
                        }
                        
                        // Hiển thị message bình thường nếu không có "Lý do:"
                        return item.message && (
                          <Text type="secondary" className={styles.message}>
                            {item.message}
                          </Text>
                        );
                      })()}
                      {/* Chỉ hiển thị note nếu không phải user-update-request, HR update và new update request (đã xử lý ở trên) */}
                      {!isUpdateRequestNotification(item) && !isHRUpdateNotification(item) && !isNewUpdateRequestNotification(item) && item.note && (
                        <div className={styles.noteContainer}>
                          <Text type="secondary" className={styles.noteLabel}>Lý do:</Text>
                          <div className={styles.noteBlock}>
                            <Text type="secondary">"{item.note}"</Text>
                          </div>
                        </div>
                      )}
                      {item.attachmentName && (
                        <div className={styles.attachmentRow}>
                          <ClockCircleOutlined className={styles.attachmentIcon} />
                          <Text className={styles.attachmentName}>{item.attachmentName}</Text>
                          {item.attachmentSize && (
                            <Text type="secondary" className={styles.attachmentSize}>
                              {item.attachmentSize}
                            </Text>
                          )}
                        </div>
                      )}
                      {renderPrimaryAction(item)}
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </div>
    </div>
  );
};
